import {
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    CardActionArea,
    Box
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import { ExternalLinkIcon } from '../../../../icons';

const AlertCard = ({
    title,
    mainCount,
    mainLabel,
    secondaryCount,
    secondaryLabel,
    link,
    loading,
    hasIssues,
    onCardClick
}) => {
    const history = useHistory();

    const cardStyle = {
        height: '100%',
        backgroundColor: hasIssues ? '#fff3e0' : undefined,
        borderLeft: hasIssues ? '4px solid #ff9800' : undefined
    };

    const handleClick = () => {
        if (link) {
            history.push(link);
            if (onCardClick) {
                onCardClick();
            }
        }
    };

    return (
        <Grid item xs={12} sm={6} md={3}>
            <Card style={cardStyle}>
                <CardActionArea onClick={handleClick} style={{ height: '100%' }}>
                    <CardContent style={{ position: 'relative' }}>
                        <Box
                            style={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: hasIssues ? '#ff9800' : '#9e9e9e',
                                fontSize: '0.875rem'
                            }}
                        >
                            <ExternalLinkIcon />
                        </Box>

                        <Typography
                            color="textSecondary"
                            gutterBottom
                            variant="subtitle2"
                            style={{
                                fontWeight: hasIssues ? 'bold' : 'normal',
                                paddingRight: '24px'
                            }}
                        >
                            {title}
                        </Typography>

                        {loading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    style={{
                                        fontWeight: 'bold',
                                        marginBottom: secondaryCount != null ? '8px' : '0',
                                        color: hasIssues ? '#e65100' : undefined
                                    }}
                                >
                                    {mainCount != null ? mainCount : '-'}{' '}
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ fontSize: '0.875rem', fontWeight: 'normal' }}
                                    >
                                        {mainLabel}
                                    </Typography>
                                </Typography>

                                {secondaryCount != null && secondaryLabel && (
                                    <Typography color="textSecondary" variant="body2">
                                        {secondaryCount} {secondaryLabel}
                                    </Typography>
                                )}
                            </>
                        )}
                    </CardContent>
                </CardActionArea>
            </Card>
        </Grid>
    );
};

const AlertCards = ({ onCardClick }) => {
    const [alertsData, alertsLoading] = useSwaggerClient(
        'users/statistics',
        'getDashboardAlerts',
        {},
        []
    );

    const [negativeBalancesData, negativeBalancesLoading] = useSwaggerClient(
        'users',
        'getNegativeBalances',
        { page: 0, pageSize: 1 },
        []
    );

    const customBookings = alertsData?.customBookings || {};
    const failedEmails = alertsData?.failedEmails || {};
    const negativeBalancesTotal = negativeBalancesData?.total ?? null;
    const expiredUnconfirmedBookingsTotal = alertsData?.totalExpiredUnconfirmedBookings ?? null;

    const hasCustomBookingIssues =
        (customBookings.count24h || 0) > 0 || (customBookings.count7d || 0) > 0;
    const hasNegativeBalanceIssues = (negativeBalancesTotal || 0) > 0;
    const hasFailedEmailIssues =
        (failedEmails.count24h || 0) > 0 || (failedEmails.count7d || 0) > 0;

    const hasExpiredUnconfirmedBookingIssues = (expiredUnconfirmedBookingsTotal || 0) > 0;

    return (
        <>
            <AlertCard
                title="Unconverted Custom Bookings"
                mainCount={customBookings.count24h}
                mainLabel="in 24h"
                secondaryCount={customBookings.count7d}
                secondaryLabel="in 7d"
                link="/client/custombookings?bookingId=unconverted"
                loading={alertsLoading}
                hasIssues={hasCustomBookingIssues}
                onCardClick={onCardClick}
            />
            <AlertCard
                title="Negative Balances"
                mainCount={negativeBalancesTotal}
                mainLabel="total"
                link="/client/negative-balances"
                loading={negativeBalancesLoading}
                hasIssues={hasNegativeBalanceIssues}
                onCardClick={onCardClick}
            />
            <AlertCard
                title="Failed Emails"
                mainCount={failedEmails.count24h ?? 0}
                mainLabel="in 24h"
                secondaryCount={failedEmails.count7d ?? 0}
                secondaryLabel="in 7d"
                link="/emaillog?emailStatus=bounce,dropped,spamreport&logType=email"
                loading={alertsLoading}
                hasIssues={hasFailedEmailIssues}
                onCardClick={onCardClick}
            />
            <AlertCard
                title="Expired unconfirmed bookings"
                mainCount={expiredUnconfirmedBookingsTotal}
                mainLabel="total"
                link="/client/expiredunconfirmedbookings"
                loading={alertsLoading}
                hasIssues={hasExpiredUnconfirmedBookingIssues}
                onCardClick={onCardClick}
            />
        </>
    );
};

export default AlertCards;
